import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Sparkles, QrCode } from "lucide-react";
import { brl } from "@/lib/format";
import { toast } from "sonner";
import { PaymentMethodDialog, PixCheckoutDialog } from "@/components/PixCheckout";
import { CancelDialog } from "@/components/Growth";

export default function Subscription() {
  const { restaurant, refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [choosing, setChoosing] = useState(null);
  const [pix, setPix] = useState(null);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/subscription"), api.get("/plans")]);
    setSub(s.data); setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  const change = async (planId) => {
    if (planId !== "free" && sub?.mp_configured) { setChoosing(plans.find((p) => p.id === planId)); return; }
    try {
      const r = await api.post("/subscription/change", { plan: planId });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      toast.success(`Plano alterado para ${planId}`); refresh(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const payCard = async () => {
    const planId = choosing.id; setChoosing(null);
    try {
      const r = await api.post("/subscription/change", { plan: planId });
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      refresh(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const payPix = async (period = "monthly") => {
    const planId = choosing.id; setChoosing(null);
    try {
      const r = await api.post("/subscription/pix", { plan: planId, period });
      setPix(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar PIX"); }
  };
  const onPaid = () => { toast.success("Pagamento confirmado! Plano ativado."); refresh(); load(); };
  const statusLabel = { approved: "Aprovado", pending: "Pendente", rejected: "Recusado", cancelled: "Cancelado", refunded: "Reembolsado" };
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancel = () => setCancelOpen(true);

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <h1 className="font-display text-3xl font-extrabold text-stone-900">Sua assinatura</h1>
      <Card><CardContent className="p-6 flex flex-wrap items-center gap-6 justify-between">
        <div>
          <div className="text-xs uppercase text-stone-500 font-semibold">Plano atual</div>
          <div className="font-display text-3xl font-extrabold text-stone-900">{sub?.plan?.name || "..."}</div>
          <div className="mt-1 flex items-center gap-2">
            <Badge className="bg-orange-100 text-orange-700 border-orange-200" variant="outline">{sub?.status}</Badge>
            {sub?.days_left != null && <span className="text-sm text-stone-500" data-testid="trial-days-left">Seu teste termina em {sub.days_left} dias</span>}
            {sub?.billing_method === "pix" && sub?.paid_until && (
              <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="pix-paid-until">
                <QrCode className="h-3.5 w-3.5" /> PIX · válido até {new Date(sub.paid_until).toLocaleDateString("pt-BR")}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase text-stone-500 font-semibold">Valor mensal</div>
          <div className="font-mono-num text-3xl font-extrabold text-orange-600">{brl(sub?.plan?.price || 0)}</div>
          {restaurant?.plan !== "free" && <Button variant="outline" size="sm" className="mt-2" onClick={cancel} data-testid="cancel-btn">Cancelar</Button>}
        </div>
      </CardContent></Card>
      {!sub?.mp_configured && (
        <Card><CardContent className="p-4 bg-amber-50 border-amber-200 text-amber-900 text-sm rounded-lg">
          <strong>Modo manual:</strong> gateway de pagamento não configurado. Alterações de plano são aplicadas imediatamente para testes.
        </CardContent></Card>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const isCurrent = restaurant?.plan === p.id;
          return (
            <Card key={p.id} className={`rounded-2xl ${isCurrent ? "border-orange-600 border-2" : ""}`} data-testid={`sub-plan-${p.id}`}>
              <CardContent className="p-6">
                {isCurrent && <Badge className="mb-2 bg-orange-600">Atual</Badge>}
                <h3 className="font-display text-xl font-extrabold">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-mono-num text-3xl font-extrabold">{brl(p.price)}</span>
                  <span className="text-stone-500 text-sm">/mês</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">{p.description}</p>
                <Button className="mt-4 w-full bg-orange-600 hover:bg-orange-700" disabled={isCurrent && sub?.billing_method !== "pix"} onClick={() => change(p.id)} data-testid={`sub-select-${p.id}`}>
                  {isCurrent ? (sub?.billing_method === "pix" ? "Renovar via PIX" : "Plano atual") : "Selecionar"}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="rounded-2xl" data-testid="payment-history">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-stone-900 mb-4">Histórico de pagamentos</h3>
          {(sub?.history || []).length === 0 ? (
            <p className="text-sm text-stone-500" data-testid="payment-history-empty">Nenhum pagamento registrado ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-stone-500">
                  <th className="py-2">Data</th><th>Método</th><th>Plano</th><th>Valor</th><th>Status</th><th className="text-right">Comprovante</th>
                </tr></thead>
                <tbody>
                  {sub.history.map((h) => (
                    <tr key={h.id} className="border-t border-stone-100" data-testid={`payment-row-${h.mp_payment_id}`}>
                      <td className="py-2">{new Date(h.created_at).toLocaleString("pt-BR")}</td>
                      <td className="uppercase">{h.method || "cartão"}</td>
                      <td className="capitalize">{h.plan || "—"}{h.days ? ` · ${h.days}d` : ""}</td>
                      <td className="font-mono-num">{brl(h.amount)}</td>
                      <td><Badge variant="outline" className={h.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-50 text-stone-600"}>{statusLabel[h.status] || h.status}</Badge></td>
                      <td className="text-right">
                        {h.receipt_url ? <a href={h.receipt_url} target="_blank" rel="noreferrer" className="text-orange-600 font-semibold hover:underline" data-testid={`receipt-${h.mp_payment_id}`}>Abrir</a> : <span className="text-stone-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <PaymentMethodDialog plan={choosing} open={!!choosing} onClose={() => setChoosing(null)} onPix={payPix} onCard={payCard} />
      <PixCheckoutDialog pix={pix} open={!!pix} onClose={() => setPix(null)} onPaid={onPaid} />
      <CancelDialog open={cancelOpen} onClose={() => setCancelOpen(false)} onDone={() => { refresh(); load(); }} />
    </div>
  );
}
